import Backbone from 'backbone'
import _ from 'lodash'
import sortNotesNewestFirst from 'scoreboard/notes/sort-notes-newest-first'
import { Note } from 'scoreboard/notes/notes'
import unwrapModel from 'scoreboard/components/unwrap-model'
import NoteForm from 'scoreboard/notes/note-form'
import ReplyForm from 'scoreboard/notes/reply-form'
import appDisplay from 'scoreboard/components/app-display'
import $ from 'jquery'
import canCreateNotes from 'scoreboard/notes/can-create-notes'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
import Initiative from 'scoreboard/initiatives/initiative'

const NotesListView = Backbone.View.extend({
	className: 'notes-list',

	/**
	 * Renders the notes for a note widget in "full list" format (see #20494).
	 * It renders one ReplyForm for each note, allowing inline replying/editing.
	 * It also opens a NoteForm in a dialog to allow adding a new note.
	 *
	 * @param {object} opt
	 *
	 * @param {NoteWidgetModel} opt.model
	 * the model for the widget
	 *
	 * @param {ScorecardNode|Initiative} opt.domainObjectModel
	 * the model for the scorecard item or initiative that the notes widget is associated with
	 *
	 * @param {array} opt.otherDomainObjects
	 * the Backbone.Model for any other scorecard items that may be associated with the notes, such
	 * as descendant scorecard items whose notes show up because showNoteDescendants=true
	 *
	 * @param {boolean} [opt.export=false]
	 * whether the view is being rendered for exports; allows things like turning relative times
	 * (1 hour ago) to absolute times (Nov 20, 2019 8:51 AM)
	 *
	 * @param {Boolean} [opt.disabled=false]
	 * indicates whether interactions should be disabled
	 * (ie whether clicking on notes pops up the dialog)
	 *
	 * @param {Boolean} [opt.guard=true] Pass false to prevent any guards from
	 * being applied (e.g. the reply form)
	 *
	 * @emits replyAdded notifies listeners when a reply has been added
	 */
	initialize(opt = {}) {
		this.options = opt
	},

	render() {
		this._noteViews = []

		// Render all parent note views, with the most recently created first
		sortNotesNewestFirst(this.options.domainObjectModel.get('notes'))
			.forEach(note => {
				let noteModel = new Note(note, { section: this.model.properties.get('section') })
				this._addNoteView(noteModel)
			})

		// Add CSS classes for each of the "showing" properties that are toggles on
		// (eg showControls > 'is-show-controls')
		_(this.model.validShowProperties())
			.map('property')
			.forEach(prop => {
				let className = 'is-' + _.kebabCase(prop)
				this.$el.toggleClass(className, !!this.model.properties.get(prop))
			})

		this.secure()

		return this
	},

	secure() {
		// If the user doesn't have permission to create/modify notes, hide all controls
		// regardless of the widget settings
		if (!canCreateNotes(this.options.domainObjectModel)) {
			this.$el.removeClass('is-show-controls')
		}
	},

	/**
	 * Opens the dialog for adding a new note
	 */
	showAddDialog() {
		let note = new Note({ domainId: this.options.domainObjectModel.id })
		note.section = this.model.properties.get('section')

		let noteForm = new NoteForm({
			model: note,
			domainObject: this.options.domainObjectModel
		})

		this.listenToOnce(note, 'sync', newNote => {
			// After the XHR is complete, add a view for the new note to the
			// top of the list
			this._addNoteView(newNote, { prepend: true })

			this.trigger('change:count', this._noteViews.length)
		})

		appDisplay.dialog(noteForm)
	},

	_addNoteView(noteModel, opt = {}) {
		this.listenTo(noteModel, 'destroy', this._removeNoteView.bind(this))

		let actualDomainObjectModel
		if (this.options.domainObjectModel.id == noteModel.get('domainId')) {
			actualDomainObjectModel = this.options.domainObjectModel
		} else {
			let actualDomainObject = _.find(this.options.otherDomainObjects, o => noteModel.get('domainId') == o.id)

			actualDomainObjectModel = this.options.section === 'scorecards'
				? new ScorecardNode(actualDomainObject)
				: new Initiative(actualDomainObject)
		}

		let v = new ReplyForm({
			model: noteModel,
			rootDomainObject: this.options.domainObjectModel,
			noteDomainObject: unwrapModel(actualDomainObjectModel),
			alwaysShowDomainObject: true,
			showReplyIcon: true,
			guard: this.options.guard
		})
		this._noteViews.push(v)

		if (opt.prepend) {
			this.$el.prepend(v.el)
		} else {
			this.$el.append(v.el)
		}
		v.render()

		let showAppliesTo = false
		if (this.model.properties.get('showNoteAppliesTo')) {
			// If calendar period is toggled on, there's always something there
			// (either a period or "All Time")
			showAppliesTo = true
		}
		if (this.model.properties.get('showNoteAbout') && v.$('.appliesTo.domain-object-label').text()) {
			// If scorecard node name is toggled on, make sure something is actually displayed;
			// if the note is directly associated with the scorecard node for this widget (instead
			// of one of its descendants), then the scorecard node's label isn't printed out
			showAppliesTo = true
		}

		v.$('.applies-to').toggleClass('is-not-displayed', !showAppliesTo)


		// Most markup for the notes widget comes form reply-form.hbs. However,
		// we're going to want to customize it a bit since the notes widget is
		// customizable in terms of what it can show and how it can show it.

		// 1. Rework the note-content a bit so that the text can wrap around the edit
		// button in certain situations. #27412
		var updateContents = () => {
			let noteContents = `
				<div class="note-contents">
					<div class="edit-button-placeholder"></div>
					<p>${v.context().text}</p>
				</div>`

			v.$('.note-contents').replaceWith(noteContents)
		}

		updateContents(v)
		v.on('replies:add edit', () => updateContents(v))

		// 2. Rework reply-content so that it's text does not overlap its edit
		// buttons on certain occasions. #30104
		var editReplyContent = view => {
			view.$('.reply').each((i, r) => {
				let reply = this.$(r)
				if (reply.find('.reply-content .edit-button-placholder') > 0) {
					return
				}
				reply.find('.reply-content').prepend('<div class="edit-button-placeholder"></div>')
			})
		}

		editReplyContent(v)
		this.listenTo(v, 'replies:add', (options) => {
			this.scrollReplyIntoView(options.reply, options.el)
			this.trigger('replyAdded')
			editReplyContent(v)
		})
	},

	_removeNoteView(noteModel) {
		let removed = _.remove(this._noteViews, v => v.model === noteModel)
		removed.forEach(v => v.remove())

		this.trigger('change:count', this._noteViews.length)
	},

	scrollReplyIntoView(replyModel, replyEl) {
		let container = this.$el.closest('.content')
		if (container.length === 1) {

			// We need the total offset for the reply relative to the widget .content div.
			// To get that, walk up through the DOM tree and sum the offsetTops together
			let checkedOffsetParents = []
			let replyTop = _([replyEl].concat($(replyEl).parentsUntil('.content').toArray()))
				.filter(p => {
					if (_.includes(checkedOffsetParents, p.offsetParent)) {
						// We only want to count the offsetTop relative to a given ancestor once,
						// because offsetTop isn't necessarily relative to its direct parent
						return false
					}

					checkedOffsetParents.push(p.offsetParent)
					return true
				})
				.map(p => p.offsetTop)
				.sum()

			let replyHeight = replyEl.offsetHeight
			let containerHeight = container[0].offsetHeight

			// If the reply isn't visible in the widget content's container
			// (ie scrolled out of view), scroll the container so that
			// the entire reply is displayed
			if (container[0].scrollTop + containerHeight < replyTop) {
				container.scrollTop(replyTop + replyHeight - containerHeight)
			}
		}
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this._noteViews, 'remove')
	}
})

export default NotesListView

NotesListView.__filename__ = 'dashboards/widgets/notes/notes-list-view.js'
